import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: 'ADMIN' | 'PASTOR' | 'LEADER' | 'MEMBER';
  provider?: 'email' | 'kakao';
  mustChangePassword?: boolean;
  token?: string; // JWT 토큰
  isAdmin?: boolean; // 관리자 여부
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginExpiry: number | null; // 로그인 만료 시간 (timestamp)
  
  // Actions
  setAuth: (user: User, accessToken: string, refreshToken: string, rememberMe?: boolean) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (userData: Partial<User>) => void;
  checkAuthExpiry: () => boolean;
  refreshAuthToken: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      loginExpiry: null,

      setAuth: (user, accessToken, refreshToken, rememberMe = true) => {
        const expiry = rememberMe ? Date.now() + (3 * 24 * 60 * 60 * 1000) : Date.now() + (24 * 60 * 60 * 1000); // 3일 또는 1일
        
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('loginExpiry', expiry.toString());
        
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
          loginExpiry: expiry,
        });
        
        console.log('🔐 자동 로그인 설정:', new Date(expiry).toLocaleString());
      },

      clearAuth: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('loginExpiry');
        
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          loginExpiry: null,
        });
        
        console.log('🔓 로그아웃 완료');
      },

      checkAuthExpiry: () => {
        const { loginExpiry } = get();
        if (!loginExpiry) return false;
        
        const isValid = Date.now() < loginExpiry;
        if (!isValid) {
          console.log('⏰ 로그인 만료됨');
          get().clearAuth();
        }
        return isValid;
      },

      refreshAuthToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return false;

        try {
          const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
          const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });

          if (response.ok) {
            const data = await response.json();
            const { user, accessToken, refreshToken: newRefreshToken } = data;
            
            get().setAuth(user, accessToken, newRefreshToken, true);
            console.log('🔄 토큰 자동 갱신 완료');
            return true;
          }
        } catch (error) {
          console.error('토큰 갱신 실패:', error);
        }

        get().clearAuth();
        return false;
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      updateUser: (userData) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...userData } });
        }
      },
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        loginExpiry: state.loginExpiry,
      }),
    }
  )
);
